import { useCallback, useEffect, useState } from "react";
import type { CourtType, Play, PlaySummary, Role, Session } from "./types";
import { api, ApiError } from "./lib/api";
import { clonePlay, createPlay } from "./lib/play";
import { loadAllDrafts, loadDraft, removeDraft } from "./lib/drafts";
import { AuthScreen } from "./components/AuthScreen";
import { LibraryPage } from "./components/LibraryPage";
import { Editor } from "./components/Editor";
import { PlayerViewer } from "./components/PlayerViewer";

type Screen = { kind: "library" } | { kind: "editor"; play: Play; initialDirty?: boolean } | { kind: "viewer"; play: Play };

function downloadJson(name: string, value: unknown) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [screen, setScreen] = useState<Screen>({ kind: "library" });
  const [plays, setPlays] = useState<PlaySummary[]>([]);
  const [drafts, setDrafts] = useState<Play[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadPlays = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.listPlays();
      setPlays(result.plays);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) setSession(null);
      else setError(caught instanceof Error ? caught.message : "战术库加载失败");
    } finally {
      setLoading(false);
    }
  }, []);

  const openPlay = useCallback(async (id: string, edit: boolean) => {
    setLoading(true);
    setError("");
    try {
      const result = await api.getPlay(id);
      if (edit) {
        const draft = loadDraft(id);
        if (draft && draft.version === result.play.version && JSON.stringify(draft) !== JSON.stringify(result.play)) {
          const restore = window.confirm("发现这套战术的本机草稿，是否继续编辑草稿？");
          setScreen({ kind: "editor", play: restore ? draft : result.play, initialDirty: restore });
        } else setScreen({ kind: "editor", play: result.play });
      } else setScreen({ kind: "viewer", play: result.play });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "无法打开战术");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.session()
      .then((result) => setSession(result))
      .catch(() => setSession(null))
      .finally(() => setCheckingSession(false));
  }, []);

  useEffect(() => {
    if (!session) return;
    setDrafts(session.role === "coach" ? loadAllDrafts() : []);
    loadPlays().then(() => {
      const sharedId = new URLSearchParams(window.location.search).get("play");
      if (sharedId) openPlay(sharedId, false);
    });
  }, [loadPlays, openPlay, session]);

  const backToLibrary = () => {
    window.history.replaceState({}, "", window.location.pathname);
    setScreen({ kind: "library" });
    setDrafts(loadAllDrafts());
    loadPlays();
  };

  const share = async (id: string) => {
    const url = new URL(window.location.href);
    url.search = "";
    url.searchParams.set("play", id);
    try {
      await navigator.clipboard.writeText(url.toString());
      setNotice("分享链接已复制；队员打开后仍需输入查看口令。 ");
    } catch {
      window.prompt("复制下面的分享链接", url.toString());
    }
  };

  if (checkingSession) return <div className="app-loading"><div className="brand-mark"><span>5</span></div><p>正在打开战术库…</p></div>;
  if (!session) return <AuthScreen onLogin={async (role: Role, password: string) => setSession(await api.login(role, password))} />;

  return (
    <>
      {screen.kind === "library" && <LibraryPage
        role={session.role}
        plays={plays}
        drafts={drafts}
        loading={loading}
        error={error}
        onRetry={loadPlays}
        onOpen={openPlay}
        onResumeDraft={(play) => setScreen({ kind: "editor", play, initialDirty: true })}
        onDeleteDraft={(id) => {
          if (!window.confirm("确定删除这份本机草稿吗？云端已保存的战术不会被删除。")) return;
          removeDraft(id);
          setDrafts(loadAllDrafts());
        }}
        onCreate={(courtType: CourtType) => setScreen({ kind: "editor", play: createPlay(courtType) })}
        onCopy={async (id) => {
          try {
            const { play } = await api.getPlay(id);
            await api.createPlay(clonePlay(play));
            await loadPlays();
            setNotice("战术副本已创建。 ");
          } catch (caught) { setError(caught instanceof Error ? caught.message : "复制失败"); }
        }}
        onDelete={async (id) => {
          const target = plays.find((play) => play.id === id);
          if (!window.confirm(`确定删除“${target?.name ?? "这套战术"}”吗？此操作不可撤销。`)) return;
          try { await api.deletePlay(id); await loadPlays(); } catch (caught) { setError(caught instanceof Error ? caught.message : "删除失败"); }
        }}
        onShare={share}
        onExport={async () => {
          try {
            const backup = await api.exportBackup();
            downloadJson(`球队战术库备份-${new Date().toISOString().slice(0, 10)}.json`, backup);
          } catch (caught) { setError(caught instanceof Error ? caught.message : "导出失败"); }
        }}
        onImport={async (file) => {
          try {
            const parsed = JSON.parse(await file.text()) as { plays?: Play[] } | Play[];
            const imported = Array.isArray(parsed) ? parsed : parsed.plays;
            if (!Array.isArray(imported)) throw new Error("备份文件中没有有效的战术列表");
            const result = await api.importBackup(imported);
            await loadPlays();
            setNotice(`已导入 ${result.imported} 套战术，原内容未被覆盖。`);
          } catch (caught) { setError(caught instanceof Error ? caught.message : "导入失败"); }
        }}
        onLogout={async () => { await api.logout(); setSession(null); setPlays([]); }}
      />}
      {screen.kind === "editor" && <Editor
        initialPlay={screen.play}
        initialDirty={screen.initialDirty}
        onBack={backToLibrary}
        onSave={async (play) => {
          try {
            const result = play.version === 0 ? await api.createPlay(play) : await api.updatePlay(play);
            setNotice("已保存到共享战术库。 ");
            return result.play;
          } catch (caught) {
            if (caught instanceof ApiError && caught.body.code === "VERSION_CONFLICT") {
              throw new Error("保存冲突：这套战术已在其他页面更新。本机草稿未丢失，请返回战术库重新打开后合并修改。");
            }
            throw caught;
          }
        }}
      />}
      {screen.kind === "viewer" && <PlayerViewer play={screen.play} onBack={backToLibrary} onShare={() => share(screen.play.id)} />}
      {notice && <div className="toast" role="status" onAnimationEnd={() => setNotice("")}>{notice}</div>}
    </>
  );
}
